import moment from "moment";

import { capitalizeFirstLetter } from "../../../../../utils";
import { LiabilitiesDetails } from "../../../types";
import { RateChangeDirection, RateRow, RateSummary } from "./types";
import { currentRateOf } from "../../../helpers";
import { toIsoDate } from "../../../../../utils/dates";

const getDirection = (delta: number): RateChangeDirection => {
  if (delta > 0) {
    return "up";
  }
  if (delta < 0) {
    return "down";
  }
  return "flat";
};

// e.g. "1y 2mo"; a period still in effect (open, or followed by a scheduled one) is measured up to today.
const buildHeldLabel = (
  start: string,
  end: string | null,
  today: string,
): string => {
  const from = moment(start);
  const to = moment(end !== null && end <= today ? end : today);
  const totalMonths = Math.max(0, to.diff(from, "months"));
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  if (years > 0 && months > 0) {
    return `${years}y ${months}mo`;
  }
  if (years > 0) {
    return `${years}y`;
  }
  if (months > 0) {
    return `${months}mo`;
  }
  return "<1mo";
};

// Newest first, each with deltas against the directly preceding (older) period.
export const buildRateRows = (
  liability: LiabilitiesDetails,
  now: Date = new Date(),
): RateRow[] => {
  const interestRateHistory = liability.interestRateHistory ?? [];
  const currentRateId = currentRateOf(interestRateHistory, now)?.id;
  const today = toIsoDate(now);

  const rows: RateRow[] = interestRateHistory.map((entry, index) => {
    const prior = index > 0 ? interestRateHistory[index - 1] : null;
    const next =
      index < interestRateHistory.length - 1
        ? interestRateHistory[index + 1]
        : null;
    const rateDeltaPp = prior ? entry.interestRate - prior.interestRate : null;
    const hasPayments =
      prior &&
      entry.paymentAmount !== null &&
      entry.paymentAmount !== undefined &&
      prior.paymentAmount !== null &&
      prior.paymentAmount !== undefined;
    const paymentDelta = hasPayments
      ? (entry.paymentAmount ?? 0) - (prior.paymentAmount ?? 0)
      : null;
    const isScheduled = entry.effectiveDate > today;

    return {
      id: entry.id,
      rate: entry.interestRate,
      effectiveDate: entry.effectiveDate,
      paymentAmount: entry.paymentAmount ?? null,
      isOrigination: index === 0,
      isCurrent: entry.id === currentRateId,
      isScheduled,
      rateDeltaPp,
      rateDirection: rateDeltaPp === null ? "flat" : getDirection(rateDeltaPp),
      paymentDelta,
      heldLabel: isScheduled
        ? null
        : buildHeldLabel(
            entry.effectiveDate,
            next ? next.effectiveDate : null,
            today,
          ),
    };
  });

  return rows.reverse();
};

export const buildRateSummary = (
  liability: LiabilitiesDetails,
  now: Date = new Date(),
): RateSummary | null => {
  const history = liability.interestRateHistory ?? [];
  if (history.length === 0) {
    return null;
  }

  const current = currentRateOf(history, now);
  const rates = history.map((entry) => entry.interestRate);

  return {
    currentRate: current?.interestRate ?? null,
    minRate: Math.min(...rates),
    maxRate: Math.max(...rates),
    periodCount: history.length,
    rateTypeLabel: capitalizeFirstLetter((current ?? history[0]).rateType),
    resetMonths: liability.resetMonths,
    nextResetDate: liability.nextResetDate,
  };
};

/** Subtitle copy, e.g. "Variable · resets every 12 months". */
export const buildSubtitle = (summary: RateSummary): string => {
  if (summary.resetMonths === null) {
    return summary.rateTypeLabel;
  }
  return `${summary.rateTypeLabel} · resets every ${summary.resetMonths} months`;
};
